import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiActivity, type ApiActivityRow } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { ago, dateTime, POLL } from "@/lib/format";

/** Live API activity: who called what, how it went, and — for rejections —
 *  exactly why (the error the caller saw). Metadata only; bodies are never
 *  stored. Mutations always logged; reads appear when rejected. */
export function ApiActivityPage() {
  const [actor, setActor] = useState("");
  const [path, setPath] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const q = useQuery({
    queryKey: ["api-activity", actor, path, errorsOnly],
    queryFn: () => apiActivity.list({ actor: actor || undefined, path: path || undefined,
      errors_only: errorsOnly ? "true" : undefined, limit: "200" }),
    refetchInterval: POLL.LIST,
  });
  const dim = <span style={{ color: "var(--t-dim)" }}>—</span>;
  const cols = useMemo<DataCol<ApiActivityRow>[]>(() => [
    { key: "ts", label: "When", width: 150, mono: true, render: (r) => dateTime(r.ts), title: (r) => ago(r.ts) },
    { key: "actor", label: "Who", width: 150, mono: true, render: (r) => r.actor },
    { key: "action", label: "Request", width: 330, mono: true, render: (r) => r.action },
    { key: "status", label: "Status", width: 70, mono: true, render: (r) => r.status == null ? dim : (
      <span style={{ color: (r.status ?? 0) < 400 ? "var(--ok)" : (r.status ?? 0) < 500 ? "var(--warn)" : "var(--bad)", fontWeight: 700 }}>{r.status}</span>) },
    { key: "ms", label: "ms", width: 60, mono: true, render: (r) => r.ms ?? dim },
    { key: "ip", label: "From", width: 120, mono: true, render: (r) => r.ip ?? dim },
    { key: "why", label: "Why rejected", width: 420, render: (r) => r.err ? (
      <span className="muted">
        {r.err.error_code && <span className="mono" style={{ color: "var(--bad)", fontWeight: 700 }}>{r.err.error_code}</span>}
        {r.err.message ? ` - ${r.err.message}` : ""}{r.err.errors?.length ? ` · ${r.err.errors.join(" · ")}` : ""}
      </span>) : dim,
      title: (r) => r.err ? [r.err.error_code, r.err.message, ...(r.err.errors ?? [])].filter(Boolean).join(" | ") : undefined },
  ], []);
  return (
    <div className="mpage on">
      <PageHead eyebrow="API · Activity" title="API activity"
        desc="Who called what, how long it took, and - for rejections - exactly the error the caller saw. Mutations are always recorded; reads appear when denied. Bodies are never stored." />
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <SearchInput placeholder="Filter by who…" value={actor} onChange={(e) => setActor(e.target.value)} style={{ width: 180 }} />
        <SearchInput placeholder="Filter by path…" mono value={path} onChange={(e) => setPath(e.target.value)} style={{ width: 240 }} />
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12.5, cursor: "pointer" }}>
          <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
          rejections only
        </label>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)" }}>{q.data ? `${q.data.rows.length} events` : ""}</span>
      </div>
      <DataTable rows={q.data?.rows ?? []} cols={cols} rowKey={(r) => r.id} pageSize={100} maxHeight={720}
        empty={<div className="panel panel-pad cap">No API activity recorded yet.</div>} />
    </div>
  );
}
